import { CoordinateArray } from 'types';

const pad = (num: number, size: number): string => {
    let s = num.toString();
    while (s.length < size) s = '0' + s;
    return s;
};

export function toDMS(
    deg: number | string,
    format?: 'D' | 'DM' | 'DMS',
    dp?: number,
    marks = false,
    lat = false
): string | null {
    if (typeof deg === 'object') throw new TypeError('Geo.toDMS - deg is [DOM?] object');
    if (isNaN(Number(deg))) return null; // give up here if we can't make a number from deg

    // default values
    if (typeof format === 'undefined') format = 'DMS';
    marks = typeof marks !== 'undefined' ? marks : true;

    if (typeof dp === 'undefined') {
        switch (format) {
            case 'D':
                dp = 4;
                break;
            case 'DM':
                dp = 2;
                break;
            case 'DMS':
                dp = 0;
                break;
            default:
                format = 'DMS';
                dp = 0; // be forgiving on invalid format
        }
    }

    deg = Math.abs(Number(deg)); // (unsigned result ready for appending compass dir'n)
    let d: string;
    let m: string;
    let s: string;
    let dms: string;
    switch (format) {
        case 'D':
            d = deg.toFixed(dp); // round degrees
            if (Number(d) < 100) d = pad(Number(d), 2); // pad with leading zeros
            if (Number(d) < 10) d = pad(Number(d), 2);
            dms = marks ? `${d}°` : d; // add º symbol
            break;
        case 'DM': {
            const min = (deg * 60).toFixed(dp); // convert degrees to minutes & round
            d = Math.floor(Number(min) / 60).toString(); // get component deg/min
            m = (Number(min) % 60).toFixed(dp); // pad with trailing zeros
            if (Number(d) < 100) d = pad(Number(d), 2); // pad with leading zeros
            if (Number(d) < 10) d = pad(Number(d), 2);
            if (Number(m) < 10) m = pad(Number(m), 2);
            dms = marks ? `${d}° ${m}'` : `${d} ${m}`; // add º, ' symbols
            break;
        }
        case 'DMS': {
            const sec = (deg * 3600).toFixed(dp); // convert degrees to seconds & round
            d = Math.floor(Number(sec) / 3600).toString(); // get component deg/min/sec
            m = (Math.floor(Number(sec) / 60) % 60).toString();
            s = (Number(sec) % 60).toFixed(dp); // pad with trailing zeros

            if (Number(m) < 10) m = pad(Number(m), 2);
            if (Number(s) < 10) s = pad(Number(s), 2);
            if (!lat) {
                if (Number(d) < 100) d = pad(Number(d), 2);
            }

            dms = marks ? `${pad(Number(d), 2)}° ${m}' ${s}"` : `${pad(Number(d), 2)} ${m} ${s}`; // add º, ' symbols
            break;
        }
        default:
            dms = '';
            break;
    }

    return dms;
}

export function formatLatitude(coord: number | undefined): string {
    if (!coord) {
        return '';
    }
    return `${toDMS(coord, 'DM', 2, true, true)} ${coord > 0 ? 'N' : 'S'}`;
}

export function formatLongitude(coord: number | undefined): string {
    if (!coord) {
        return '';
    }
    return `${toDMS(coord, 'DM', 2, true, true)} ${coord > 0 ? 'E' : 'W'}`;
}

export function formatCoordinate(coordinate: CoordinateArray | []) {
    if (!coordinate) {
        return coordinate;
    }

    return [formatLatitude(coordinate[1]), formatLongitude(coordinate[0])];
}
